import React, { Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { User, Settings, ChevronRight, BarChart2, Store } from 'lucide-react';
import { useAppStore } from '../../shared/store';
import { BulkrrLogo } from '../../shared/components/BulkrrLogo';
export function ProfilePage() {
  const navigate = useNavigate();
  const {
    monthlyBudget,
    transactions
  } = useAppStore();
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();
  const spent = transactions.filter(t => {
    const date = new Date(t.date);
    return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
  }).reduce((sum, t) => sum + t.amount, 0);
  const menuItems = [{
    icon: Store,
    label: 'Winkels',
    value: 'Beheer je winkels',
    color: 'text-success',
    bg: 'bg-success/10',
    onClick: () => navigate('/stores')
  }, {
    icon: BarChart2,
    label: 'Mijn uitgaven',
    value: 'Bekijk je overzicht',
    color: 'text-warning',
    bg: 'bg-warning/10',
    onClick: () => navigate('/analytics')
  }, {
    icon: Settings,
    label: 'Instellingen',
    value: 'Budget, meldingen en weergave',
    color: 'text-muted-foreground',
    bg: 'bg-accent',
    onClick: () => navigate('/settings')
  }];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 10
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.2
      }
    }
  };
  return <div className="min-h-screen bg-background pb-24" data-id="element-1917" data-mp="289v606vqpqmp">
      <div className="px-6 pt-6" data-id="element-1918" data-mp="3oomvoc1i4jsb">
        <div className="flex justify-center mb-8" data-id="element-1919" data-mp="3onl8xab4i8ur">
          <BulkrrLogo variant="full" size="md" data-id="element-1920" data-mp="3jquv6tmsjpci" />
        </div>

        {/* Profile Card */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="mb-6 rounded-2xl border border-primary bg-primary p-6 text-primary-foreground shadow-lg shadow-primary/20" data-id="element-1921" data-mp="3qtrumerboyoo">
          <div className="flex items-center gap-4 mb-6" data-id="element-1922" data-mp="9kd1on8aui1d">
            <div className="w-16 h-16 bg-primary-foreground/20 rounded-full flex items-center justify-center backdrop-blur-sm" data-id="element-1923" data-mp="9j1vkgrdsy73">
              <User className="w-8 h-8" data-id="element-1924" data-mp="3d1ldoqgcino6" />
            </div>
            <div data-id="element-1925" data-mp="3d00iqzq4d2ps">
              <h2 className="text-xl font-semibold" data-id="element-1926" data-mp="3i17rp24007y1">Welkom terug</h2>
              <p className="text-sm font-medium text-primary-foreground/80" data-id="element-1927" data-mp="3hzwnj16n4y6e">
                Jouw overzicht voor deze maand
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-primary-foreground/20" data-id="element-1928" data-mp="2pbr72qmmdvcd">
            <div data-id="element-1929" data-mp="2uac6woxymd0a">
              <p className="text-primary-foreground/80 text-sm font-medium mb-1" data-id="element-1930" data-mp="2u9snt4wkpiko">
                Deze Maand
              </p>
              <p className="text-2xl font-bold" data-id="element-1931" data-mp="1be69gdb8n4kc">€{spent.toFixed(0)}</p>
            </div>
            <div data-id="element-1932" data-mp="17ct3m385b1df">
              <p className="text-primary-foreground/80 text-sm font-medium mb-1" data-id="element-1933" data-mp="12fubtrbyf6ie">
                Budget Over
              </p>
              <p className="text-2xl font-bold" data-id="element-1934" data-mp="xhj2dhpkmy0p">
                €{(monthlyBudget - spent).toFixed(0)}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Menu Items */}
        <motion.div variants={containerVariants} initial="hidden" animate="show" className="space-y-2" data-id="element-1935" data-mp="nqoj8m7mgejh">
          {menuItems.map((item, index) => {
          const Icon = item.icon;
          return <Fragment key={index} data-id="element-1936" data-mp="eof6rqssmb3g" data-mpi={index}>
                <motion.button variants={itemVariants} onClick={item.onClick} className="w-full bg-card border border-border rounded-2xl p-4 flex items-center gap-4 hover:bg-accent hover:border-primary/30 transition-all group" data-id="element-1937" data-mp="9rf0pfvy18cc">
                  <div className={`p-2.5 rounded-xl ${item.bg} ${item.color} group-hover:scale-110 transition-transform`} data-id="element-1938" data-mp="29tobv6um7gwv">
                    <Icon className="w-5 h-5" data-id="element-1939" data-mp="203lk19z8c904" />
                  </div>
                  <div className="flex-1 text-left" data-id="element-1940" data-mp="201scztkpfmrl">
                    <p className="font-medium text-foreground" data-id="element-1941" data-mp="1m3ap9vusua9o">{item.label}</p>
                    {item.value && <p className="text-sm text-muted-foreground mt-0.5" data-id="element-1942" data-mp="1r3yeqd3dqj1s">
                        {item.value}
                      </p>}
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:translate-x-1 transition-transform" data-id="element-1943" data-mp="1h3xu8ntrxww0" />
                </motion.button>

              </Fragment>;
        })}
        </motion.div>

      </div>
    </div>;
}